import React, {useState} from 'react';

import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircleIcon from '@mui/icons-material/Circle';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import CustomChip from '../common/CustomChip';
import CustomBox from '../common/CustomBox';
import CustomDialog from '../common/CustomDialog';
import SessionTypeFormV2 from './SessionTypeV2Form';
import SessionTypeDetailsV2 from './SessionTypeDetailsV2';

import useIsMobile from '../../hooks/useIsMobile';
import config from '../../config/config';
import {getServiceCategories} from '../../helpers/selectOptions';
import {calculateSessionCharge} from '../../helpers/sessions';
import {formatCurrency} from '../../helpers/currency';

const ListItem = ({
  sessionType, 
  selected, 
  onSelect,
}) => {
  const match = getServiceCategories()
    .find(c => c.value === sessionType.service_category);
  if (!match) return null;
  const {color, label} = match || {};
  return (
    <Box
      style={{
        border: selected ? `1px solid ${color}` : '1px solid rgb(245, 245, 245)',
        borderLeft: selected ? `4px solid ${color}` : '1px solid rgb(245, 245, 245)',
        cursor: 'pointer',
        padding: '1rem',
        minHeight: '80px',
      }}
      sx={{'&:hover': {boxShadow: 1}}}
      onClick={e => onSelect(e, sessionType.id)}
    >
      <Stack>
        <Stack direction='row' spacing={1}>
          <CircleIcon style={{fontSize: '1rem', color}}/>
          <Stack spacing={.5} style={{width: '100%'}}>
            <Stack direction='row' justifyContent='space-between' style={{width: '100%'}}>
              <Typography variant='h2'>
                {
                  label
                  ||  sessionType.name
                }
              </Typography>
              <CustomChip
                variant='outlined'
                label={sessionType.is_physical_therapy ? 'Restore' : 'Optimize'}
                style={{color, borderColor: color}}
              />
            </Stack>
            <Stack direction='row' alignItems='center' spacing={2}>
              <Typography variant='body2' color='text.muted'>
                Eval:{' '}
                {formatCurrency(
                    calculateSessionCharge({
                    rate: sessionType.eval_hourly_rate,
                    duration: sessionType.eval_duration_minutes,
                    discount: 0,
                    case: null,
                  }))
                }
                {' '}({sessionType.eval_duration_minutes} min)
              </Typography>
              <Typography variant='body2' color='text.muted'>
                Follow-up:{' '}
                {formatCurrency(
                    calculateSessionCharge({
                    rate: sessionType.hourly_rate,
                    duration: sessionType.duration_minutes,
                    discount: 0,
                    case: null,
                  })
                )}
                {' '}({sessionType.duration_minutes} min)
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

const ListSessionTypesV2Screen = ({
  sessionTypes = [],
  defaultSelectedId = null,
}) => {
  const isMobile = useIsMobile();
  const [newSessionTypeOpen, setSessionTypeOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(
    isMobile
    ? false
    : (defaultSelectedId || sessionTypes?.[0]?.id)
  );
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <CustomBox>
          <Stack>
            <Stack 
              direction='row' 
              alignItems='center' 
              justifyContent='space-between'
              style={{paddingRight: '1rem'}}
            >
              <Box style={{padding: '1rem'}}>
                <Typography variant='h1'>Your Service Types</Typography>
              </Box>
              <Box>
                <Button 
                  variant='contained' 
                  size='small'
                  onClick={() => setSessionTypeOpen(true)}
                > 
                  <AddIcon />
                  New
                </Button>
              </Box>
            </Stack>
            {
              !sessionTypes?.length && 
              <Box style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
                <Typography variant='body1' color='text.muted' style={{marginLeft: '1rem'}}>
                  You don't have any session types yet. Create session types to define the services you offer, set pricing, and manage availability.
                </Typography>
              </Box>
            }
            {sessionTypes.map(st => 
              <ListItem
                key={st.id}
                sessionType={st}
                selected={isMobile 
                  ? false 
                  : selectedId ? selectedId === st.id : st.id === sessionTypes[0].id
                }
                onSelect={(e, id) => setSelectedId(id)}
              />
            )}
          </Stack>
        </CustomBox>
      </Grid>
      <Grid item md={8} sx={{display: {xs: 'none', md: 'block'}}}>
        {!!selectedId && 
          <CustomBox style={{padding: '1rem'}}>
            <SessionTypeDetailsV2 
              sessionType={sessionTypes.find(st => st.id === selectedId)} 
            />
          </CustomBox>
        }
      </Grid>
      {isMobile && 
        <CustomDialog
          open={!!selectedId}
          onClose={() => setSelectedId(null)}
          fullWidth
          maxWidth='md'
          title='Session Type Details'
        >
          <CircleIcon 
            style={{
              fontSize: '1rem', 
              color: getServiceCategories().find(c => c.value === sessionTypes.find(st => st.id === selectedId)?.service_category)?.color
            }}
          />
          <Typography variant='h2'>
            {
              getServiceCategories().find(c => c.value === sessionTypes.find(st => st.id === selectedId)?.service_category)?.label
              ||  sessionTypes.find(st => st.id === selectedId)?.name
            }
          </Typography>
          <SessionTypeFormV2
            key={selectedId} // force remount when selectedId changes
            isEditing={true}
            sessionType={sessionTypes.find(st => st.id === selectedId)}
          />
        </CustomDialog>
      }
      <CustomDialog
        open={newSessionTypeOpen}
        title='New Service Type'
        onClose={() => setSessionTypeOpen(false)}
        fullWidth
        maxWidth='md'
      >
        <SessionTypeFormV2
          isEditing={false}
          serviceCategoriesUsed={sessionTypes.map(st => st.service_category)}
          onSuccess={() => setSessionTypeOpen(false)} 
        />
      </CustomDialog>
    </Grid>
  );

};

export default ListSessionTypesV2Screen;